/* eslint-disable no-restricted-globals */
import _ from 'underscore';
import cookie from 'js-cookie';

export type EventType = 'message' | 'action';

export interface ICustomizedEvent {
  eventType: EventType;
  eventData: any;
  eventSource?: string;
}

export interface ISessionInfo {
  isOpen?: boolean;
  token?: string;
  customizedEvent?: ICustomizedEvent;
}

export interface ISessionManager {
  saveSessionInfo(info: ISessionInfo): Promise<ISessionInfo | null>;
  getSessionInfo(): Promise<ISessionInfo | null>;
  clearSessionInfo(): Promise<void>;
}

type SessionInfoEventHandler = (info: any, key: string) => void;

export interface ISessionManagerOptions {
  sessionInfoKey: string;
  onSaveSessionInfo?: SessionInfoEventHandler;
  onGetSessionInfo?: SessionInfoEventHandler;
  onClearSessionInfo?: SessionInfoEventHandler;
}

export abstract class SessionManager implements ISessionManager {
  protected sessionInfoKey: string;

  protected sessionInfo: ISessionInfo | null = null;

  protected onSaveSessionInfo: SessionInfoEventHandler;

  protected onGetSessionInfo: SessionInfoEventHandler;

  protected onClearSessionInfo: SessionInfoEventHandler;

  constructor({
    sessionInfoKey,
    onSaveSessionInfo = _.noop,
    onGetSessionInfo = _.noop,
    onClearSessionInfo = _.noop,
  }: ISessionManagerOptions) {
    this.sessionInfoKey = sessionInfoKey;
    this.onSaveSessionInfo = onSaveSessionInfo;
    this.onGetSessionInfo = onGetSessionInfo;
    this.onClearSessionInfo = onClearSessionInfo;
  }

  public saveSessionInfo(_info: ISessionInfo): Promise<ISessionInfo> {
    return Promise.reject(new Error('Not implemented'));
  }

  public getSessionInfo(): Promise<ISessionInfo | null> {
    return Promise.reject(new Error('Not implemented'));
  }

  public clearSessionInfo(): Promise<void> {
    return Promise.reject(new Error('Not implemented'));
  }
}

export interface ICookieSessionManagerOptions extends ISessionManagerOptions {
  domain: string;
}

export class CookieSessionManager extends SessionManager {
  private domain: string;

  constructor({
    domain,
    ...args
  }: ICookieSessionManagerOptions) {
    super(args);
    this.domain = domain;
  }

  public saveSessionInfo(newInfo: ISessionInfo) {
    this.sessionInfo = _.extend({}, this.sessionInfo, newInfo);
    cookie.set(this.sessionInfoKey, JSON.stringify(this.sessionInfo), {
      domain: this.domain,
      secure: true,
    });
    this.onSaveSessionInfo(this.sessionInfo, this.sessionInfoKey);
    return Promise.resolve(newInfo);
  }

  public getSessionInfo() {
    let result;

    try {
      if (this.sessionInfo == null) {
        this.sessionInfo = JSON.parse(cookie.get(this.sessionInfoKey) as string);
      }
      result = this.sessionInfo;
    } catch (e) {
      result = null;
    }

    this.onGetSessionInfo(result, this.sessionInfoKey);
    return Promise.resolve(result);
  }

  public clearSessionInfo() {
    this.onClearSessionInfo(this.sessionInfo, this.sessionInfoKey);
    cookie.remove(this.sessionInfoKey);
    return Promise.resolve();
  }
}
